import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { OnrampApiError } from '../client.js';
import { fundingNavigationManifest } from '../generated/navigation.js';
import { bindOnrampCache } from '../operations/cache-lifecycle.js';
import { withdrawalContextQuery } from './context-query.js';
import { isWithdrawalMethod } from './types.js';
export function useWithdrawalCheckout(client, surface, createId, active = true, initialCurrency = 'USD', locale = 'en', realtime) {
  const cache = useQueryClient();
  bindOnrampCache(client, cache);
  const [currency, setCurrency] = useState(initialCurrency);
  const [screen, setScreen] = useState('payouts');
  const [method, setMethod] = useState(null);
  const [bankPreference, setBankPreference] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(null);
  const [operationId, setOperationId] = useState(null);
  const [dismissedId, setDismissedId] = useState(null);
  const [hostedUrl, setHostedUrl] = useState(null);
  const [authorityExpired, setAuthorityExpired] = useState(false);
  const key = useRef(null);
  const pendingCreate = useRef(null);
  const signed = useRef(null);
  const authorityGeneration = useRef(0);
  const resumeOnActivation = useRef(false);
  const context = useQuery({
    ...withdrawalContextQuery(client, currency, surface),
    enabled: active
  });
  const candidateId = operationId ?? context.data?.pendingId ?? null;
  const activeId = candidateId === dismissedId ? null : candidateId;
  const operation = useQuery({
    queryKey: ['onramp', client.cacheKey, 'operation', activeId],
    queryFn: ({
      signal
    }) => {
      if (!activeId) throw new Error('offramp_operation_missing');
      return client.withdrawal(activeId, signal);
    },
    enabled: active && activeId !== null,
    meta: {
      persist: false
    },
    refetchInterval: query => !active || query.state.data && ['delivered', 'failed', 'cancelled', 'refunded'].includes(query.state.data.status) ? false : 7000
  });
  const resumed = useQuery({
    queryKey: ['onramp', client.cacheKey, 'withdrawal-checkout', activeId, operation.data?.withdrawal?.providerId],
    queryFn: ({
      signal: signal_0
    }) => {
      if (!activeId) throw new Error('offramp_operation_missing');
      return client.resumeWithdrawal(activeId, signal_0);
    },
    enabled: active && activeId !== null && !hostedUrl && !authorityExpired && operation.data?.withdrawal?.kind === 'sell',
    staleTime: 30000,
    meta: {
      persist: false
    }
  });
  useEffect(() => {
    if (!active || !activeId || !realtime) return;
    return realtime.subscribe(id => {
      if (id === activeId) void cache.invalidateQueries({
        queryKey: ['onramp', client.cacheKey, 'operation', id],
        exact: true
      });
    });
  }, [active, activeId, realtime, cache, client]);
  const commands = useMutation({
    mutationFn: async command => {
      if (command.kind === 'quote') {
        if (pendingCreate.current) {
          await createOperation(pendingCreate.current.amountD6, preview);
          return;
        }
        setAmount(command.amountD6);
        if (method === 'manual_withdraw') setScreen('review');else if (method) {
          const quoted = await cache.fetchQuery({
            queryKey: ['onramp', client.cacheKey, 'withdrawal-preview', method, surface, currency, command.amountD6],
            staleTime: 15000,
            meta: {
              persist: false
            },
            queryFn: ({
              signal: signal_1
            }) => client.quoteWithdrawal({
              method,
              amountD6: command.amountD6,
              currency,
              surface
            }, signal_1)
          });
          setPreview(quoted);
          await createOperation(command.amountD6, quoted);
        }
      } else if (command.kind === 'create') {
        if (activeId) {
          if (method === 'manual_withdraw' || operation.data?.withdrawal?.kind === 'manual') {
            setScreen('status');
            return;
          }
          setAuthorityExpired(true);
          setHostedUrl(null);
          const generation = authorityGeneration.current;
          const refreshed = await resumed.refetch({
            throwOnError: true
          });
          if (generation !== authorityGeneration.current) return;
          setHostedUrl(refreshed.data?.url ?? null);
          setAuthorityExpired(false);
          setScreen(refreshed.data?.url ? 'payout_account' : 'status');
          return;
        }
        await createOperation(amount, preview);
      } else if (command.kind === 'send') {
        if (!activeId) throw new Error('offramp_operation_missing');
        const reviewed = operation.data;
        if (!reviewed) throw new Error('offramp_review_missing');
        const reserved = await client.reserveWithdrawal(activeId, reviewed.version, true);
        if (!reserved.executable) {
          await operation.refetch();
          setScreen('status');
          return;
        }
        setScreen('status');
        let authorizationAttempted = false;
        const signature = await command.send(reserved.operation.destination, reserved.recipient, reserved.amountD6, reserved.operation.network, activeId, reserved.mint, async () => {
          if (authorizationAttempted) throw new Error('offramp_submission_unknown');
          authorizationAttempted = true;
          const authorization = await client.reserveWithdrawal(activeId, reserved.operation.version);
          if (!authorization.executable) throw new Error('offramp_review_changed');
        });
        signed.current = {
          id: activeId,
          signature
        };
        await client.reportWithdrawal(activeId, signature);
        signed.current = null;
        await operation.refetch();
      } else if (command.kind === 'report' && signed.current) {
        await client.reportWithdrawal(signed.current.id, signed.current.signature);
        signed.current = null;
        await operation.refetch();
      } else if (activeId) {
        await client.cancelWithdrawal(activeId);
        await operation.refetch();
      }
    }
  });
  const {
    mutate,
    isPending
  } = commands;
  const withdrawalKind = operation.data?.withdrawal?.kind;
  const abandoned = useRef(new Set());
  function leaveOperation(unusedOnly = false) {
    const current = operation.data;
    if (!activeId || !current || commands.isPending || pendingCreate.current) return;
    const unused = !current.withdrawal?.sendReserved && !current.withdrawal?.signature && (!current.withdrawal?.providerStatus || current.withdrawal.providerStatus === 'waitingForDeposit') && signed.current?.id !== activeId && ['created', 'awaiting_payment'].includes(current.status);
    if (unusedOnly && !unused) return;
    authorityGeneration.current += 1;
    setDismissedId(activeId);
    setOperationId(null);
    setHostedUrl(null);
    setAuthorityExpired(true);
    setScreen('payouts');
    setMethod(null);
    setPreview(null);
    setAmount('');
    setRecipient('');
    setBankPreference(null);
    key.current = null;
    // Backend cancellation serializes against send reservation; never infer cancellation
    // from iframe dismissal or cancel a transfer already authorized for broadcast.
    if (unused && !abandoned.current.has(activeId)) {
      abandoned.current.add(activeId);
      void client.cancelWithdrawal(activeId).then(() => context.refetch()).catch(() => {
        client.observeFunnel?.({
          stage: 'status',
          outcome: 'error',
          method: null,
          operationId: current.id,
          errorCode: 'withdrawal_cancel_failed'
        });
      });
    }
  }
  const leaveUnusedOperation = useEffectEvent(() => leaveOperation(true));
  useEffect(() => {
    if (!active && activeId && operation.data && !commands.isPending) leaveUnusedOperation();
  }, [active, activeId, operation.data, commands.isPending]);
  useEffect(() => {
    if (!active) {
      authorityGeneration.current += 1;
      resumeOnActivation.current = true;
      setHostedUrl(null);
      setAuthorityExpired(true);
      return;
    }
    if (!resumeOnActivation.current) return;
    if (!activeId) {
      if (!context.isPending) resumeOnActivation.current = false;
      return;
    }
    if (withdrawalKind !== 'sell' || isPending) return;
    resumeOnActivation.current = false;
    if (screen === 'payout_account') mutate({
      kind: 'create'
    });
  }, [active, activeId, context.isPending, withdrawalKind, isPending, screen, mutate]);
  async function createOperation(amountD6, quoted_0) {
    if (!method) throw new Error('offramp_method_missing');
    const idempotencyKey = key.current ?? createId();
    key.current = idempotencyKey;
    // Recover the exact request after an ambiguous response, never create a second order.
    pendingCreate.current ??= {
      idempotencyKey,
      method,
      amountD6,
      currency,
      surface,
      locale,
      ...(bankPreference ? {
        bankPreference: bankPreference.id
      } : {}),
      ...(method === 'manual_withdraw' ? {
        recipient
      } : quoted_0 ? {
        payoutUnits: quoted_0.payoutUnits,
        feeUnits: quoted_0.feeUnits
      } : {})
    };
    const generation_0 = authorityGeneration.current;
    const result = await client.createWithdrawal(pendingCreate.current).catch(failure => {
      // Validation/auth rejection occurs before persistence; allow a corrected request.
      if (failure instanceof OnrampApiError && [400, 401, 403, 422].includes(failure.status)) {
        pendingCreate.current = null;
        key.current = null;
      }
      throw failure;
    });
    setOperationId(result.operation.id);
    cache.setQueryData(['onramp', client.cacheKey, 'operation', result.operation.id], result.operation);
    if (generation_0 === authorityGeneration.current) {
      setHostedUrl(result.url);
      setAuthorityExpired(false);
    }
    setScreen(result.url ? 'payout_account' : 'status');
    pendingCreate.current = null;
  }
  function select(next, bank) {
    if (commands.isPending || activeId || pendingCreate.current) return;
    setMethod(next);
    setBankPreference(bank ?? null);
    setPreview(null);
    key.current = null;
    setScreen(fundingNavigationManifest.offramp.events[next === 'manual_withdraw' ? 'manual_selected' : 'method_selected']);
  }
  function back() {
    if (commands.isPending || pendingCreate.current) return;
    if (activeId) {
      leaveOperation();
      return;
    }
    const destination = fundingNavigationManifest.offramp.screens[screen].back;
    if (destination) setScreen(method === 'manual_withdraw' && screen === 'amount' ? 'recipient' : destination);
  }
  return {
    context,
    operation,
    activeId,
    hostedUrl: !active || authorityExpired ? null : hostedUrl ?? resumed.data?.url ?? null,
    screen: activeId && screen !== 'payout_account' && screen !== 'payouts' ? 'status' : screen,
    method: method ?? (operation.data && isWithdrawalMethod(operation.data.method) ? operation.data.method : null),
    bankPreference,
    recipient,
    setRecipient,
    amount,
    preview,
    currency,
    setCurrency: next_0 => {
      if (commands.isPending || activeId || pendingCreate.current) return;
      setCurrency(next_0);
      setPreview(null);
      key.current = null;
      pendingCreate.current = null;
    },
    finish: () => {
      authorityGeneration.current += 1;
      resumeOnActivation.current = false;
      setOperationId(null);
      setHostedUrl(null);
      setAuthorityExpired(false);
      setScreen('payouts');
      setMethod(null);
      setAmount('');
      setRecipient('');
      setPreview(null);
      setBankPreference(null);
      key.current = null;
      pendingCreate.current = null;
      signed.current = null;
      commands.reset();
      void context.refetch();
    },
    canReport: signed.current !== null,
    resumeError: resumed.error,
    retryOperation: () => {
      if (commands.isPending) return;
      void operation.refetch();
      if (operation.data?.withdrawal?.kind === 'sell') commands.mutate({
        kind: 'create'
      });
    },
    select,
    back,
    recipientReady: () => setScreen('amount'),
    commands
  };
}